import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { ApiError, post } from "@/lib/api";
import { S } from "@/lib/strings";
import { cn } from "@/lib/utils";
import type { SchemaValidation } from "@/types/fleet";

const DEBOUNCE_MS = 400;

/**
 * JSON Schema field for `TaskSpec.extractionSchema`.
 *
 * Phase 1 is a plain mono textarea with a line-number gutter — no Monaco, no
 * ajv. **Validation lives on the server** (`POST /api/v1/tasks/validate-schema`),
 * which is worth being explicit about: the device validates extractions against
 * this schema using its own implementation, so a browser-side validator would be
 * a *second* opinion that can disagree with the one that actually matters. One
 * implementation, and it is the authoritative one. It also keeps ~2 MB out of
 * the bundle.
 *
 * The server's reply doubles as the preview: `columns` is what this schema will
 * actually produce downstream, which is the question the operator is really
 * asking when they write it.
 */
export function SchemaEditor({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  disabled?: boolean;
}) {
  const text = value ?? "";
  const [debounced, setDebounced] = useState(text);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(text), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [text]);

  const trimmed = debounced.trim();

  const validation = useQuery<SchemaValidation, ApiError>({
    queryKey: ["validate-schema", trimmed],
    queryFn: () => post<SchemaValidation>("/tasks/validate-schema", { schema: trimmed }),
    enabled: trimmed.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const lineCount = useMemo(() => Math.max(text.split("\n").length, 1), [text]);
  const errorLines = useMemo(() => {
    // The server reports a JSON-pointer-ish `path`, not a line. Highlighting the
    // exact line would need a position-tracking parser; listing the errors under
    // the box is honest and enough.
    return validation.data?.errors ?? [];
  }, [validation.data]);

  return (
    <div className="space-y-2">
      <div
        className={cn(
          "flex overflow-hidden rounded-md border bg-[rgba(255,255,255,0.03)] transition-colors",
          errorLines.length > 0 ? "border-[rgba(239,68,68,0.35)]" : "border-[var(--color-border)]",
          "focus-within:border-[var(--color-accent)]",
        )}
      >
        <div
          ref={gutterRef}
          aria-hidden
          className="select-none overflow-hidden border-r border-[var(--color-border-subtle)] bg-[rgba(0,0,0,0.2)] px-2 py-2 text-right font-mono text-[12px] leading-[1.55] text-[var(--color-fg-faint)]"
        >
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>

        <textarea
          ref={taRef}
          value={text}
          disabled={disabled}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          onScroll={() => {
            // Keep the gutter aligned with the text as it scrolls.
            if (gutterRef.current && taRef.current) {
              gutterRef.current.scrollTop = taRef.current.scrollTop;
            }
          }}
          onKeyDown={(e) => {
            // Tab indents rather than leaving the field: this is a code box, and
            // Shift+Tab / Escape still get you out.
            if (e.key === "Tab" && !e.shiftKey) {
              e.preventDefault();
              const el = e.currentTarget;
              const { selectionStart: a, selectionEnd: b } = el;
              const next = text.slice(0, a) + "  " + text.slice(b);
              onChange(next);
              requestAnimationFrame(() => {
                el.selectionStart = el.selectionEnd = a + 2;
              });
            }
          }}
          placeholder={'{\n  "type": "object",\n  "properties": {\n    "temp_high": { "type": "number" }\n  }\n}'}
          className="min-h-[200px] w-full resize-y bg-transparent px-3 py-2 font-mono text-[12px] leading-[1.55] text-[var(--color-fg)] placeholder:text-[var(--color-fg-faint)] focus:outline-none"
          aria-label={S.fieldSchema}
        />
      </div>

      <p className="text-[11px] text-[var(--color-fg-faint)]">{S.fieldSchemaHint}</p>

      {/* ---- server verdict ---- */}
      {trimmed.length > 0 && (
        <div>
          {validation.isFetching && (
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--color-fg-subtle)]">
              <Loader2 size={11} className="animate-spin" />
              {S.schemaValidating}
            </div>
          )}

          {!validation.isFetching && validation.isError && (
            <div className="flex items-start gap-1.5 text-[11px] text-[#fca5a5]">
              <AlertCircle size={11} className="mt-0.5 shrink-0" />
              {validation.error.message}
            </div>
          )}

          {!validation.isFetching && validation.data && (
            <>
              {errorLines.length > 0 ? (
                <ul className="space-y-1">
                  {errorLines.map((e, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#fca5a5]">
                      <AlertCircle size={11} className="mt-0.5 shrink-0" />
                      <span>
                        <span className="font-mono text-[var(--color-fg-faint)]">
                          {e.path || "/"}
                        </span>{" "}
                        {e.message}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center gap-1.5 text-[11px] text-[#10b981]">
                  <Check size={11} />
                  {S.schemaValid}
                </div>
              )}

              {validation.data.ok && (
                <div className="mt-2 rounded-md border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] p-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
                    {S.schemaColumns}
                  </div>
                  {validation.data.columns.length === 0 ? (
                    <p className="mt-1 text-[11px] text-[var(--color-fg-faint)]">
                      {S.schemaNoColumns}
                    </p>
                  ) : (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {validation.data.columns.map((c) => (
                        <span
                          key={c.name}
                          className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] bg-[rgba(255,255,255,0.03)] px-1.5 py-0.5 font-mono text-[11px]"
                        >
                          <span className="text-[var(--color-fg)]">{c.name}</span>
                          <span className="text-[var(--color-fg-faint)]">{c.type}</span>
                          {c.required && <span className="text-[#f59e0b]">*</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
